<template>
	<div class="form">
		<div class="delete" v-if="canDelete" @click="confirm"></div>

		<b-form-row>
			<b-col cols="12" md="6">
				<input-component
					name="fullName"
					label="Nome completo"
					:required="true"
					v-model="fullName"
					maxlength="255" />
			</b-col>

			<b-col cols="12" md="6">
				<input-component name="role" label="Cargo" :required="true" v-model="role" maxlength="100" />
			</b-col>
		</b-form-row>

		<b-form-row>
			<b-col cols="12" md="6">
				<input-component
					name="email"
					label="E-mail"
					:required="true"
					:check="true"
					v-model="email"
					maxlength="255" />
			</b-col>

			<b-col cols="12" md="6">
				<input-component name="linkedin" label="Perfil do LinkedIn" v-model="linkedin" maxlength="255" />
			</b-col>
		</b-form-row>

		<b-form-row>
			<b-col cols="12" md="6">
				<input-component
					name="cpf"
					label="CPF"
					:required="true"
					:mask="'###.###.###-##'"
					:check="true"
					class="mb-0"
					v-model="cpf"
					maxlength="20" />
			</b-col>

			<b-col cols="12" md="6">
				<input-component
					name="birthday"
					label="Data de nascimento"
					:required="true"
					:check="true"
					:mask="'##/##/####'"
					class="mb-0"
					v-model="birthday"
					maxlength="20" />
			</b-col>
		</b-form-row>

		<files-component
			ref="representative"
			:fileType="'representative'"
			:files="[]"
			:required="true"
			:showIndex="false"
			:uploadedFiles="$store.state.form.uploadedDocuments.representative"
			:modal-id="'modal-confirm-document'"
			:loopIndex="formKey"
			label="Documento que comprove a representação legal" />

		<confirm-modal-component type="document" modal-id="modal-confirm-document" />
		<confirm-modal-component type="contact" modal-id="modal-confirm-contact" />
	</div>
</template>

<script>
import { email, required } from "vuelidate/lib/validators";
import InputComponent from "@/components/Form/Inputs/InputComponent";
import FilesComponent from "@/components/Form/Inputs/FilesComponent";
import ConfirmModalComponent from "@/components/Form/ConfirmModalComponent";

export default {
	name: "RepresentativeFormComponent",
	props: { formKey: Number, canDelete: Boolean, salesforceContactId: String },
	components: {
		InputComponent,
		FilesComponent,
		ConfirmModalComponent,
	},
	mounted() {
		if (!this.$store.state.form.ownersAndContacts.representative[this.formKey].fullName) {
			return;
		}

		this.$v.$touch();
	},
	computed: {
		fullName: {
			get() {
				return this.$store.state.form.ownersAndContacts.representative[this.formKey].fullName;
			},
			set(value) {
				this.$store.commit("setContactData", {
					form: "representative",
					key: "fullName",
					index: this.formKey,
					value,
				});
			},
		},
		role: {
			get() {
				return this.$store.state.form.ownersAndContacts.representative[this.formKey].role;
			},
			set(value) {
				this.$store.commit("setContactData", {
					form: "representative",
					key: "role",
					index: this.formKey,
					value,
				});
			},
		},
		email: {
			get() {
				return this.$store.state.form.ownersAndContacts.representative[this.formKey].email;
			},
			set(value) {
				this.$store.commit("setContactData", {
					form: "representative",
					key: "email",
					index: this.formKey,
					value,
				});
			},
		},
		linkedin: {
			get() {
				return this.$store.state.form.ownersAndContacts.representative[this.formKey].linkedin;
			},
			set(value) {
				this.$store.commit("setContactData", {
					form: "representative",
					key: "linkedin",
					index: this.formKey,
					value,
				});
			},
		},
		cpf: {
			get() {
				return this.$store.state.form.ownersAndContacts.representative[this.formKey].cpf;
			},
			set(value) {
				this.$store.commit("setContactData", {
					form: "representative",
					key: "cpf",
					index: this.formKey,
					value,
				});
			},
		},
		birthday: {
			get() {
				return this.$store.state.form.ownersAndContacts.representative[this.formKey].birthday;
			},
			set(value) {
				this.$store.commit("setContactData", {
					form: "representative",
					key: "birthday",
					index: this.formKey,
					value,
				});
			},
		},
		representativeFileCount() {
			const representative = this.$store.state.form.ownersAndContacts.representative[this.formKey];
			const documentOrder = representative?.documentOrder || [];
			const pendingDeletes = this.$store.state.form.pendingDeletes?.representative || [];
			const pendingFiles = this.$store.state.form.pendingFiles?.representative || [];
			const fromDocumentOrder = documentOrder
				.filter(
					(doc) =>
						doc.form === "representative" &&
						(doc.representativeIndex === this.formKey || doc.representativeIndex === undefined),
				)
				.filter((doc) => !this.isDocPendingDelete(doc, pendingDeletes));
			const fromPending = pendingFiles.filter((pendingFile) => pendingFile.loopIndex === this.formKey);
			return fromDocumentOrder.length + fromPending.length;
		},
		representativeFiles() {
			return this.representativeFileCount;
		},
	},
	methods: {
		isDocPendingDelete(doc, pendingDeletes) {
			if (!pendingDeletes || !pendingDeletes.length) return false;
			const docId = doc.salesforceFileId || doc.id;
			const docName = doc.name || doc.filename;
			return pendingDeletes.some((pendingDelete) => {
				const loopMatch = pendingDelete.loopIndex === this.formKey || pendingDelete.loopIndex == null;
				if (!loopMatch) {
					return false;
				}

				const deleteId =
					pendingDelete.salesforceFileId || pendingDelete.file?.salesforceFileId || pendingDelete.file?.id;
				if (docId && deleteId && docId === deleteId) {
					return true;
				}
				
				const deleteName = pendingDelete.name || pendingDelete.file?.name || pendingDelete.file?.filename;
				if (docName && deleteName && docName === deleteName) {
					return true;
				}
				return false;
			});
		},
		confirm() {
			this.$store.commit("setConfirmForm", {
				form: "representative",
				key: this.formKey,
				salesforceContactId: this.salesforceContactId,
				formKey: this.formKey,
			});

			this.$bvModal.show("modal-confirm-contact");
		},
	},
	validations: {
		fullName: {
			required,
		},
		role: {
			required,
		},
		email: {
			required,
			email,
		},
		linkedin: {},
		cpf: {
			required,
			isValidCPF(cpf) {
				return this.$root.isValidCPF(cpf);
			},
		},
		birthday: {
			required,
			isValidDate(date) {
				return this.$root.isValidDate(date);
			},
		},
		representativeFiles: {
			atLeastOne(value) {
				return value >= 1;
			},
		},
	},
};
</script>

<style lang="scss">
.form {
	border-radius: 8px;
	border: 2px solid #e7e7e7;
	padding: 40px 40px 15px 40px;
	margin: 20px 0;
	position: relative;
}

.delete {
	content: " ";
	top: -10px;
	right: -10px;
	width: 25px;
	z-index: 99;
	height: 25px;
	position: absolute;
	border-radius: 50%;
	background-color: #ed5417;
	cursor: pointer;
}

.delete::after {
	content: "−";
	color: white;
	position: absolute;
	font-weight: bold;
	margin-left: 0.45rem;
	margin-top: 0.1rem;
}
</style>
